import React from 'react';
import { RM3_RIBBON } from 'arbitrip-common/client/utils/HotelDealsRm3';

/**
 * Inline icons of the RM3 "Choose Your Room" surface (24×24 viewBox). Colour is ALWAYS `currentColor`:
 * the caller's SCSS class decides it, so brand theming and the fixed ribbon palette both stay in CSS.
 */

const DEFAULT_SIZE = 16;
const STROKE_WIDTH = 1.8;
const STROKE_WIDTH_THIN = 1.7;
const STROKE_WIDTH_BOLD = 2;

const StrokeSvg = ({ size = DEFAULT_SIZE, strokeWidth = STROKE_WIDTH, className, children }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

const FillSvg = ({ size = DEFAULT_SIZE, path, className }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={path} fill="currentColor" fillRule="evenodd" />
    </svg>
);

const TOP_PICK_PATHS = {
    [RM3_RIBBON.VALUE]:
        'M2 3a1 1 0 0 1 1-1h8.6a2 2 0 0 1 1.4.6l8.4 8.4a2 2 0 0 1 0 2.8l-7.6 7.6a2 2 0 0 1-2.8 0L2.6 13A2 2 0 0 1 2 11.6V3zm5.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
    [RM3_RIBBON.TERMS]:
        'M12 2 4 5v6c0 5 3.4 9.3 8 11 4.6-1.7 8-6 8-11V5l-8-3zm-1.2 13.4-3.5-3.5 1.4-1.4 2.1 2.1 4.6-4.6 1.4 1.4-6 6z',
    [RM3_RIBBON.FC]:
        'M12 2l2.4 1.8 3-.2.9 2.9 2.5 1.7-1 2.8 1 2.8-2.5 1.7-.9 2.9-3-.2L12 22l-2.4-1.8-3 .2-.9-2.9-2.5-1.7 1-2.8-1-2.8 2.5-1.7.9-2.9 3 .2L12 2zm-1.2 13.4 6-6-1.4-1.4-4.6 4.6-2.1-2.1-1.4 1.4 3.5 3.5z',
    [RM3_RIBBON.LOW]:
        'M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm-1 5v7.6l-3.3-3.3-1.4 1.4L12 18.4l5.7-5.7-1.4-1.4-3.3 3.3V7h-2z',
};

export const IconTopPick = ({ kind, size }) => <FillSvg size={size} path={TOP_PICK_PATHS[kind]} />;

export const IconChevron = ({ size = 24, className }) => (
    <StrokeSvg size={size} strokeWidth={STROKE_WIDTH_BOLD} className={className}>
        <path d="m9 6 6 6-6 6" />
    </StrokeSvg>
);

export const IconPhotos = ({ size = 14 }) => (
    <StrokeSvg size={size}>
        <rect x="3" y="5" width="18" height="15" rx="2" />
        <path d="m3 15 5-4 4 3 4-4 5 5" />
    </StrokeSvg>
);

export const IconSize = () => (
    <StrokeSvg>
        <path d="M3 8V3h5M21 8V3h-5M3 16v5h5M21 16v5h-5" />
    </StrokeSvg>
);

export const IconSleeps = () => (
    <StrokeSvg>
        <path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3" />
        <path d="M22 20v-2a4 4 0 0 0-3-3.8" />
        <path d="M16 4.2a4 4 0 0 1 0 5.6" />
    </StrokeSvg>
);

export const IconBed = ({ size = DEFAULT_SIZE }) => (
    <StrokeSvg size={size}>
        <path d="M2 8v11M2 13h20v6M22 19v-6a3 3 0 0 0-3-3h-8v3" />
        <circle cx="7" cy="10" r="2" />
    </StrokeSvg>
);

export const IconNonRefundable = ({ size = 20 }) => (
    <StrokeSvg size={size} strokeWidth={STROKE_WIDTH_THIN}>
        <circle cx="12" cy="12" r="9" />
        <path d="m9 9 6 6M15 9l-6 6" />
    </StrokeSvg>
);

export const IconMeal = ({ size = 20 }) => (
    <StrokeSvg size={size} strokeWidth={STROKE_WIDTH_THIN}>
        <path d="M17 8h1a3 3 0 0 1 0 6h-1" />
        <path d="M3 8h14v5a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8Z" />
        <path d="M6 2v3M10 2v3M14 2v3" />
    </StrokeSvg>
);

export const IconRoomOnly = ({ size = 20 }) => <IconBed size={size} />;

export const IconReset = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_BOLD}>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5" />
    </StrokeSvg>
);

const IconAirConditioning = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <path d="M12 2v20M4.9 6.5l14.2 11M19.1 6.5 4.9 17.5M2 12h20" />
    </StrokeSvg>
);

const IconWifi = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <path d="M5 12.5a10 10 0 0 1 14 0M2 9a15 15 0 0 1 20 0M8.5 16a5 5 0 0 1 7 0" />
        <path d="M12 20h.01" />
    </StrokeSvg>
);

const IconTv = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <rect x="2" y="6" width="20" height="13" rx="2" />
        <path d="m8 3 4 3 4-3" />
    </StrokeSvg>
);

const IconRoomService = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <path d="M3 17h18M4.5 17a7.5 7.5 0 0 1 15 0M12 6.5V9" />
        <circle cx="12" cy="5" r="1.2" />
    </StrokeSvg>
);

const IconShower = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <path d="M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9Z" />
    </StrokeSvg>
);

const IconCheck = () => (
    <StrokeSvg strokeWidth={STROKE_WIDTH_THIN}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </StrokeSvg>
);

// English-name regex only (spec §12.3): amenity names carry no category, so an unmatched name gets the check.
const PERK_ICONS = [
    [/air.?condition|climate/i, IconAirConditioning],
    [/wi-?fi|internet/i, IconWifi],
    [/\btv\b|television/i, IconTv],
    [/room service|minibar|mini-bar|coffee|tea/i, IconRoomService],
    [/shower|bath|toilet|towel|toiletr/i, IconShower],
];

export const IconAmenity = ({ name }) => {
    const hit = PERK_ICONS.find(([pattern]) => pattern.test(String(name || '')));
    const Icon = hit ? hit[1] : IconCheck;
    return <Icon />;
};
